import { Table as OvermapTable, Text } from "@overmap-ai/blocks"
import { flexRender } from "@tanstack/react-table"
import { cx } from "class-variance-authority"

import { genericMemo } from "../../utils/optimization"
import type { ExtendableTableProps } from "./typings"

interface TableProps<TData> extends ExtendableTableProps<TData> {
	onRowClick?: (data: TData) => void
	interactive: boolean
	emptyText: string
}

export const Table = genericMemo(function <TData>(props: TableProps<TData>) {
	const { ref, table, onRowClick, interactive, emptyText, ...rest } = props

	return (
		<OvermapTable.Root ref={ref} {...rest}>
			<OvermapTable.Header>
				{table.getHeaderGroups().map((headerGroup) => (
					<OvermapTable.Row key={headerGroup.id}>
						{headerGroup.headers.map((header) => (
							<OvermapTable.ColumnHeaderCell key={header.id} colSpan={header.colSpan}>
								<div className="flex w-max items-center gap-1">
									{header.isPlaceholder
										? null
										: flexRender(header.column.columnDef.header, header.getContext())}
								</div>
							</OvermapTable.ColumnHeaderCell>
						))}
					</OvermapTable.Row>
				))}
			</OvermapTable.Header>

			<OvermapTable.Body>
				{table.getRowModel().rows.length > 0 ? (
					table.getRowModel().rows.map((row) => (
						<OvermapTable.Row
							key={row.id}
							className={cx({ "hover:bg-(--base-a3) cursor-pointer": interactive })}
							onClick={() => onRowClick?.(row.original)}
						>
							{row.getVisibleCells().map((cell) => (
								<OvermapTable.Cell key={cell.id} className="w-max align-middle">
									<div className="w-max">
										{flexRender(cell.column.columnDef.cell, cell.getContext())}
									</div>
								</OvermapTable.Cell>
							))}
						</OvermapTable.Row>
					))
				) : (
					<OvermapTable.Row>
						<OvermapTable.Cell colSpan={table.getAllColumns().length}>
							<Text accentColor="base">{emptyText}</Text>
						</OvermapTable.Cell>
					</OvermapTable.Row>
				)}
			</OvermapTable.Body>
		</OvermapTable.Root>
	)
})
